import { useAtom, useAtomValue } from "jotai";
import { useState, useEffect } from "react";
import {
  listingGuidAtom,
  passengersAtom,
  selectedDateAtom,
  selectedSeasonAtom,
  promoCodeAtom,
  isOrderCreatingAtom,
  customerDetailsAtom,
  currentCalendarMonthDateAtom,
} from "./store";
import {
  BookingType,
  Client,
  CreateMarketplaceBookingCommand,
  GetOrCreateCustomerByEmailCommand,
  PricingMode,
  Query,
} from "@repo/rq-api-public";
import { keepPreviousData } from "@tanstack/react-query";
import { isSameDay, startOfMonth, endOfMonth, addMonths } from "date-fns";
import { TimeOnly } from "@repo/rq-api";
import { useListingQuery } from "../../api/hooks";
import Calendar from "./Calendar";
import BookingSummary from "./BookingSummary";
import PromoCodeCollapsible from "./PromoCodeCollapsible";
import TextField from "../ui/TextField";
import TextArea from "../ui/TextArea";
import PhoneInput from "../ui/PhoneInput";
import Button from "../ui/Button";
import Loading from "../ui/Loading";
import { ErrorIcon } from "../ui/icons";
import { cn } from "../../styles/utils";
import { textStyles } from "../../styles/typography";
import { CustomerDetails } from "./types";
import { handleApiErrors } from "../../utils/apiErrors";
import { ApiValidationError } from "@repo/rq-api/customTypes";
import { captureEvent } from "@sentry/react";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { getWidgetDomain } from "../../utils/url";
import ExistingCustomerEmailSent from "./ExistingCustomerEmailSent";

type FieldNames = "name" | "email" | "phone" | "message";
type ValidationError = ApiValidationError<FieldNames>;

export default function SingleStepEnquiryView() {
  const [isOrderCreating, setIsOrderCreating] = useAtom(isOrderCreatingAtom);
  const [passengers] = useAtom(passengersAtom);
  const [customerDetails, setCustomerDetails] = useAtom(customerDetailsAtom);

  const listingGuid = useAtomValue(listingGuidAtom);
  const selectedDate = useAtomValue(selectedDateAtom);
  const [selectedSeason, setSelectedSeason] = useAtom(selectedSeasonAtom);
  const promoCode = useAtomValue(promoCodeAtom);

  const month = useAtomValue(currentCalendarMonthDateAtom);
  const { data: listing } = useListingQuery();
  // InstantlyBookable listings show both "Book now" and "Send enquiry" CTAs.
  // RequestOnly listings show only "Send enquiry".
  // Both CTAs create an Enquiry booking and redirect to the customer portal.
  const isInstantlyBookable =
    listing?.bookingType === BookingType.InstantlyBookable;
  const isCalendarMode = listing?.pricingMode === PricingMode.Calendar;

  const { data: calendarDaySeasons } =
    Query.useListingsGuidCalendarPricingGETQuery(
      {
        guid: listingGuid!,
        startDate: startOfMonth(month),
        endDate: endOfMonth(addMonths(month, 1)),
      },
      {
        enabled: isCalendarMode && !!listingGuid,
        placeholderData: keepPreviousData,
      },
    );

  // Sync the selected season atom whenever the user picks a date or the calendar pricing data refreshes
  useEffect(() => {
    if (!selectedDate || !calendarDaySeasons?.dayPrices) {
      setSelectedSeason(null);
      return;
    }
    const season = calendarDaySeasons.dayPrices.find((s) =>
      isSameDay(s.date, selectedDate),
    );
    setSelectedSeason(season?.isAvailable ? season : null);
  }, [selectedDate, calendarDaySeasons, setSelectedSeason]);

  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [generalErrors, setGeneralErrors] = useState<unknown | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    [key in FieldNames]?: ValidationError;
  }>({});
  const [showExistingCustomerEmailSent, setShowExistingCustomerEmailSent] =
    useState(false);

  const { mutateAsync: createCustomer } = Query.useCustomersPOSTMutation();
  const { mutateAsync: createBooking } = Query.useBookingsPOSTMutation();

  const clearError = (name: FieldNames) =>
    setFieldErrors((errors) => ({ ...errors, [name]: undefined }));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (Object.values(fieldErrors).filter(Boolean).length > 0) return;
    if (!selectedSeason) return;

    setIsOrderCreating(true);
    setGeneralErrors(null);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData) as CustomerDetails;
    data.phone = phoneNumber!;
    setCustomerDetails(data);

    try {
      // 1. Create customer
      window.parent.postMessage(
        { ...data, trackEvent: "customerDetails" },
        "*",
      );

      let customerGuid: string | undefined;
      let isExisting = false;
      try {
        const customerResult = await createCustomer(
          new GetOrCreateCustomerByEmailCommand({
            name: data.name,
            email: data.email,
            phone: data.phone,
          }),
        );
        customerGuid = customerResult.customerGuid;
        isExisting = customerResult.isExistingCustomer ?? false;
      } catch (error) {
        handleApiErrors<FieldNames>(setGeneralErrors, (validationError) => {
          setFieldErrors((prev) => ({
            ...prev,
            [validationError.field as string]: validationError,
          }));
        })(error);
        return;
      }

      // 2. Create booking — backend hardcodes Status = Enquiry for marketplace bookings.
      //    For existing customers: backend sends a magic link email and we show "Check your email" instead of redirecting.
      //    For new customers: we redirect straight to the customer portal.
      let redirectUrl: string;
      try {
        const bookingResult = await createBooking(
          new CreateMarketplaceBookingCommand({
            name: data.name,
            email: data.email,
            phone: data.phone,
            customerGuid: customerGuid!,
            date: selectedDate,
            listingGuid: listingGuid!,
            passengers,
            pricingGuid: null,
            // Calendar mode: backend uses platform ChargeableDayThresholdTime; startTime value is ignored.
            startTime: new TimeOnly({ hour: 0, minute: 0 }),
            notes: data.message,
            promoCode: promoCode?.submittedCode ?? undefined,
            widgetDomain: getWidgetDomain(),
            isExistingCustomer: isExisting,
          }),
        );

        // Existing customer: show "check your email" screen instead of redirecting
        if (isExisting) {
          setShowExistingCustomerEmailSent(true);
          return;
        }

        const urlResult = await Client.bookingsBookingguidPublicUrlGET(
          bookingResult.bookingGuid!,
          customerGuid,
          bookingResult.widgetAccessToken ?? undefined,
        );
        redirectUrl =
          urlResult.bookingDetailUrl ??
          `https://booking.getonpropeller.com/bookings/${bookingResult.bookingGuid}`;
      } catch (error) {
        handleApiErrors(setGeneralErrors)(error);
        captureEvent({
          message: "Failed to create enquiry booking",
          level: "info",
          extra: { error, listingGuid, selectedDate, passengers },
        });
        return;
      }

      // 3. Redirect the parent page to the customer portal
      if (window.parent !== window) {
        window.parent.postMessage(
          { redirect: true, booknow: true, redirectTo: redirectUrl },
          "*",
        );
      } else {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      const e = error as Error;
      setGeneralErrors(e?.message ?? error);
      captureEvent({
        message: e?.message ?? "Enquiry booking error",
        level: "info",
        extra: { error },
      });
    } finally {
      setIsOrderCreating(false);
    }
  };

  if (isOrderCreating) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (showExistingCustomerEmailSent) {
    return <ExistingCustomerEmailSent />;
  }

  return (
    <div className="flex flex-col gap-6 p-5 overflow-auto max-w-[400px] mx-auto">
      {/* Date selection */}
      <div className="flex flex-col items-center">
        <Calendar showLegend={false} className="mt-0" />
      </div>

      {/* Contact info */}
      <div>
        <form
          className="flex flex-col gap-3"
          onSubmit={handleSubmit}
          id="enquiryForm"
        >
          <TextField
            placeholder="First and Last Name"
            name="name"
            required
            defaultValue={customerDetails?.name}
            error={fieldErrors["name"]?.message}
            onChange={() => clearError("name")}
            className="px-4 py-2.5"
          />
          <TextField
            placeholder="E-mail Address"
            name="email"
            type="email"
            pattern="[A-Za-z0-9._+\-']+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}"
            required
            defaultValue={customerDetails?.email}
            error={fieldErrors["email"]?.message}
            onChange={() => clearError("email")}
            className="px-4 py-2.5"
          />
          <PhoneInput
            placeholder="Phone Number"
            defaultCountry="US"
            value={phoneNumber}
            onChange={(value) => {
              setPhoneNumber(value);
              if (value && !isPossiblePhoneNumber(value)) {
                setFieldErrors((prev) => ({
                  ...prev,
                  phone: { field: "phone", message: "Invalid phone number" },
                }));
              } else {
                setFieldErrors((prev) => ({ ...prev, phone: undefined }));
              }
            }}
            defaultValue={customerDetails?.phone}
            error={fieldErrors["phone"]?.message}
            required
            className="px-4 py-2.5"
          />
          <TextArea
            placeholder="Note"
            name="message"
            rows={1}
            defaultValue={customerDetails?.message}
            error={fieldErrors["message"]?.message}
            onChange={() => clearError("message")}
            className="px-4 py-2.5"
          />
        </form>
      </div>

      {/* Promo code */}
      <PromoCodeCollapsible />

      {/* Pricing breakdown */}
      <BookingSummary showTitle={false} showCharterDate={false} compact />

      {/* Errors */}
      {generalErrors ? (
        <div className="border rounded-lg px-5 py-4 text-red bg-redLight">
          <div className={cn(textStyles.body1, "flex gap-2 mb-2 font-medium")}>
            <ErrorIcon /> Something went wrong
          </div>
          {JSON.stringify(generalErrors)}
        </div>
      ) : null}

      {/* CTAs — both buttons create the same Enquiry booking */}
      <div className="flex flex-col gap-3 pb-6">
        {isInstantlyBookable && (
          <Button
            type="submit"
            form="enquiryForm"
            variant="primary"
            className="w-full"
            disabled={isOrderCreating || !selectedSeason}
          >
            Book now
          </Button>
        )}
        <Button
          type="submit"
          form="enquiryForm"
          variant={isInstantlyBookable ? "secondary" : "primary"}
          className="w-full"
          disabled={isOrderCreating || !selectedSeason}
        >
          Send enquiry
        </Button>
      </div>
    </div>
  );
}
